import React, { forwardRef } from "react";
import {
  Ellipsis,
  Typography,
  TypographyColors,
  TypographyVariants
} from "../../../components/Typography";
import {
  NavbarDropdownMenu,
  type NavbarDropdownMenuProps
} from "../NavbarDropdownMenu";
import { classNames } from "../../../utils/common";

import styles from "./UserProfile.module.css";

// This is a wrapper around NavbarDropdownMenu to make it easier to use
// in the Navbar component.
// Also used to filter this component in the Navbar component.
export const NavbarUserProfile = (props: NavbarDropdownMenuProps) => {
  return <NavbarDropdownMenu {...props} />;
};

type NavbarUserProfileTriggerProps = {
  name?: string;
  email: string;
  avatar: React.ReactElement;
  onClick?: () => void;
};

type NavbarUserProfileTriggerInternalProps = {
  expanded: boolean;
  isActive: boolean;
  onPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
};

export const NavbarUserProfileTrigger = forwardRef<
  HTMLButtonElement,
  NavbarUserProfileTriggerProps
>((props, ref) => {
  const { name, email, avatar, onClick, ...internalProps } = props;

  const {
    isActive,
    expanded: isExpanded,
    onKeyDown,
    onPointerDown
  } = internalProps as NavbarUserProfileTriggerInternalProps;

  const ariaProps: React.AriaAttributes = Object.entries(internalProps)
    .filter(([restPropKey]) => restPropKey.includes("aria-"))
    .reduce((ariaProps, [ariaKey, ariaValue]) => {
      return { ...ariaProps, [ariaKey]: ariaValue };
    }, {});

  const dataProps = Object.entries(internalProps)
    .filter(([restPropKey]) => restPropKey.includes("data-"))
    .reduce((ariaProps, [ariaKey, ariaValue]) => {
      return { ...ariaProps, [ariaKey]: ariaValue };
    }, {});

  return (
    <div className={styles.userProfileTriggerWrapper}>
      <button
        className={classNames({
          [styles.userProfile]: true,
          [styles.userProfileActive]: isActive
        })}
        onClick={onClick}
        {...ariaProps}
        {...dataProps}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        ref={ref}
      >
        <div className={styles.userProfileContentWrapper}>
          <div className={styles.avatarWrapper}>{avatar}</div>

          {isExpanded && (
            <div className={styles.userDetailsWrapper}>
              {name && (
                <Typography
                  color={TypographyColors.NEUTRAL_800}
                  variant={TypographyVariants.TITLE_MD}
                  className={classNames({
                    [styles.userDetailsOverflowWrapper]: true,
                    [styles.userDetailsTitle]: true
                  })}
                  as="span"
                >
                  <Ellipsis>{name}</Ellipsis>
                </Typography>
              )}
              <Typography
                color={
                  !name
                    ? TypographyColors.NEUTRAL_800
                    : TypographyColors.NEUTRAL_600
                }
                variant={
                  !name
                    ? TypographyVariants.TITLE_MD
                    : TypographyVariants.BODY_BOLD
                }
                className={classNames({
                  [styles.userDetailsOverflowWrapper]: true,
                  [styles.userDetailsDescription]: !!name,
                  [styles.userDetailsTitle]: !name
                })}
                as="span"
              >
                <Ellipsis>{email}</Ellipsis>
              </Typography>
            </div>
          )}
        </div>
      </button>
    </div>
  );
});
